import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../home/Icon';
import IconTextRow from '../home/IconTextRow';
import Tooltip from '../tooltip/Tooltip';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import { groupRoleLabel, resolveGroupAndRole } from '../../util/participantGroups';
import { mailSlotLabel } from '../../util/mailSlots';
import { formatEuro } from '../../util/payment';
import { scoreClass } from '../../util/matchScore';
import './MatchPill.css';

// Group (+ role) of a match candidate at the event date: "Orga · Mitglied" ('' while unknown).
// Candidates carry participantGroup (ref) + role.
// Role names as configured in /groups › Rollen.
export function useCandidateGroupLabel(candidate) {
  const { byId } = useParticipantGroups();
  useRoleSettings();
  const { group, role } = resolveGroupAndRole(candidate, byId);
  return groupRoleLabel(group, role);
}

// Status labels of a Sammelanmeldung outcome that differs from the backend preview (see approvalSummary).
const APPROVAL_OUTCOME_LABEL = {
  confirmed: 'Bestätigt',
  waitlist: 'Warteliste',
  addOnWaitlist: 'Zusatz-Warteliste',
};

// Inbox preview of what approving this candidate does: "Bei Annahme: 20,00 € · Anmeldung bestätigt".
// mailOnApproval is the mail slot (confirmed / waitlist / equipment_waitlist = Zusatz-Warteliste /
// payment_request) or null = no automatic mail. The backend simulates the full admission of this single
// registration (event full, add-on waitlist, reductions — the price includes the booked add-ons).
// `outcome` is only set inside a Sammelanmeldung, when a member's real approval differs from that single
// simulation (members in registration order, status chosen in the group dialog): { status, reduced } —
// status ('confirmed' | 'waitlist' | 'addOnWaitlist') when the status differs (its mail is not previewed, so only
// a neutral hint), reduced when earlier members leave less of an add-on. Either way the previewed price is stale:
// it is replaced by a recalculation hint (the backend prices the member on approval).
// null when the response carries no preview (candidate without group) or the candidate is already in the
// event (the approval changes nothing).
export function approvalSummary(candidate, outcome = null) {
  if (!candidate || candidate.alreadyInEvent) return null;
  if (candidate.priceOnApproval == null && candidate.participantGroup == null) return null;
  const parts = [];
  if (candidate.priceOnApproval != null && !outcome) parts.push(formatEuro(candidate.priceOnApproval));
  if (outcome?.status) parts.push(`${APPROVAL_OUTCOME_LABEL[outcome.status] ?? outcome.status} (Mail laut Gruppenregel)`);
  else parts.push(candidate.mailOnApproval ? mailSlotLabel(candidate.mailOnApproval) : 'keine Mail');
  if (outcome && candidate.priceOnApproval != null) {
    parts.push(outcome.reduced
      ? 'Preis wird bei Annahme neu berechnet (gekürzte Zusätze)'
      : 'Preis wird bei Annahme neu berechnet');
  }
  return `Bei Annahme: ${parts.join(' · ')}`;
}

// Meta line below a candidate name in pick lists: group at the event date, then either the approval
// preview (inbox, `approval`, with the differing Sammelanmeldung `outcome` if any) or the plain standard price
// (add-player flow, `price`).
export function CandidateGroupMeta({ candidate, approval = false, outcome = null, price = null }) {
  const groupLabel = useCandidateGroupLabel(candidate);
  const parts = [groupLabel];
  if (approval) parts.push(approvalSummary(candidate, outcome));
  else if (price != null) parts.push(`Preis ${formatEuro(price)}`);
  const text = parts.filter(Boolean).join(' · ');
  return text ? <span className="candidateGroupMeta">{text}</span> : null;
}

// Pill mit Score, Name und Tooltip. Klick expandiert Details inline.
// Ausgegliedert aus Registrations.js, damit die Page-Komponente schlanker bleibt.
// approvalOutcome: see approvalSummary `outcome` (null = the backend preview holds; set only for Sammelanmeldung
// members).
export const MatchPill = ({ m, mc, expanded, onToggle, approvalOutcome = null }) => {
  const [localExpanded, setLocalExpanded] = useState(false);
  useEffect(() => { if (expanded) setLocalExpanded(true); }, [expanded]);
  const groupLabel = useCandidateGroupLabel(m);
  const approval = approvalSummary(m, approvalOutcome);

  const tooltipContent = localExpanded ? null : (
    <div className="registrationCardChipTooltip">
      <span className="registrationCardChipTooltipName">{m.firstName} {m.lastName}</span>
      <span className="registrationCardChipTooltipScore">Score: {m.score}</span>
      {groupLabel && (
        <IconTextRow icon="groups" iconClassName="registrationCardChipIcon">Gruppe am Spieltag: {groupLabel}</IconTextRow>
      )}
      {approval && (
        <IconTextRow icon="how_to_reg" iconClassName="registrationCardChipIcon">{approval}</IconTextRow>
      )}
      {m.email && (
        <IconTextRow icon="mail" iconClassName="registrationCardChipIcon">{m.email}</IconTextRow>
      )}
      {m.phone && (
        <IconTextRow icon="call" iconClassName="registrationCardChipIcon">{m.phone}</IconTextRow>
      )}
      {m.memo && (
        <IconTextRow icon="note_stack" iconClassName="registrationCardChipIcon memo">{m.memo}</IconTextRow>
      )}
      {m.alreadyInEvent && (
        <IconTextRow icon="warning" iconClassName="registrationCardChipIcon warn" tone="warn" bold>
          Bereits im Spieltag
        </IconTextRow>
      )}
      {m.previouslyInEvent && !m.alreadyInEvent && (
        <IconTextRow icon="history" iconClassName="registrationCardChipIcon restore" tone="info" bold>
          Wurde entfernt — wird wiederhergestellt
        </IconTextRow>
      )}
      {m.diffs?.length > 0 && (
        <IconTextRow icon="warning" iconClassName="registrationCardChipIcon diff" tone="diff" bold>
          Abweichend: {m.diffs.join(', ')}
        </IconTextRow>
      )}
      {m.namesSwapped && (
        <IconTextRow icon="swap_horiz" iconClassName="registrationCardChipIcon swap" tone="diff">
          Vor-/Nachname vertauscht?
        </IconTextRow>
      )}
    </div>
  );

  return (
    <Tooltip text={tooltipContent}>
      <span
        className={`registrationCardMatch ${mc} ${m.alreadyInEvent ? 'alreadyInEvent' : ''} ${m.previouslyInEvent && !m.alreadyInEvent ? 'previouslyInEvent' : ''} ${m.diffs?.length > 0 && !m.alreadyInEvent ? 'hasDiffs' : ''} ${localExpanded ? 'expanded' : ''}`}
        onClick={onToggle}
      >
        <div className="registrationCardMatchHeader">
          <ParticipantGroupDot row={m} withTooltip={false} />
          {m.alreadyInEvent && <Icon className="registrationCardChipIcon warn" icon="warning" />}
          {m.previouslyInEvent && !m.alreadyInEvent && <Icon className="registrationCardChipIcon restore" icon="history" />}
          {m.diffs?.length > 0 && !m.alreadyInEvent && <Icon className="registrationCardChipIcon diff" icon="warning" />}
          {m.memo && <Icon className="registrationCardChipIcon memo" icon="note_stack" />}
          {m.namesSwapped && <Icon className="registrationCardChipIcon swap" icon="swap_horiz" />}
          <span className="registrationCardMatchName">{m.firstName} {m.lastName}</span>
          <span className={`registrationCardScore ${mc}`}>{m.score}</span>
        </div>
        <AnimatePresence onExitComplete={() => setLocalExpanded(false)}>
          {expanded && (
            <motion.div
              className="registrationCardMatchDetails"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {groupLabel && (
                <span className="registrationCardMatchDetailRow">
                  <Icon className="registrationCardChipIcon" icon="groups" />
                  Gruppe am Spieltag: {groupLabel}
                </span>
              )}
              {approval && (
                <span className="registrationCardMatchDetailRow">
                  <Icon className="registrationCardChipIcon" icon="how_to_reg" />
                  {approval}
                </span>
              )}
              {m.email && (
                <span className="registrationCardMatchDetailRow">
                  <Icon className="registrationCardChipIcon" icon="mail" />
                  {m.email}
                </span>
              )}
              {m.phone && (
                <span className="registrationCardMatchDetailRow">
                  <Icon className="registrationCardChipIcon" icon="call" />
                  {m.phone}
                </span>
              )}
              {m.memo && (
                <span className="registrationCardMatchDetailRow">
                  <Icon className="registrationCardChipIcon" icon="note_stack" />
                  {m.memo}
                </span>
              )}
              {m.alreadyInEvent && (
                <span className="registrationCardMatchDetailRow warn">
                  <Icon className="registrationCardChipIcon" icon="warning" />
                  Bereits im Spieltag
                </span>
              )}
              {m.previouslyInEvent && !m.alreadyInEvent && (
                <span className="registrationCardMatchDetailRow restore">
                  <Icon className="registrationCardChipIcon" icon="history" />
                  Wurde entfernt — wird beim Annehmen wiederhergestellt
                </span>
              )}
              {m.diffs?.length > 0 && (
                <span className="registrationCardMatchDetailRow diff">
                  <Icon className="registrationCardChipIcon" icon="warning" />
                  {m.diffs.join(', ')} {m.diffs.length === 1 ? 'weicht' : 'weichen'} ab
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </span>
    </Tooltip>
  );
};

// Liste von MatchPills. Hält State, welcher Eintrag derzeit ausgeklappt ist.
export const MatchPillGroup = ({ matches, approvalOutcome = null }) => {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div className="registrationCardMatches">
      <span className="registrationCardMatchesLabel">
        <Icon className="registrationCardChipIcon" icon="person_search" />
        {matches.length === 1 ? 'Vorschlag:' : `Vorschläge (${matches.length}):`}
      </span>
      {matches.map(m => (
        <MatchPill
          key={m.playerId}
          m={m}
          mc={scoreClass(m.score)}
          expanded={expandedId === m.playerId}
          approvalOutcome={approvalOutcome}
          onToggle={() => setExpandedId(id => id === m.playerId ? null : m.playerId)}
        />
      ))}
    </div>
  );
};
